import { Temporal } from '@js-temporal/polyfill'
import { getLocalAsUtcInstant } from './getLocalAsUtcInstant'

/**
 * Campaign-like item with optional endDate and title (API ISO string, Temporal, or Date for compat).
 */
export interface CampaignWithEndDate {
  endDate?: string | Temporal.Instant | Date | null
  title?: string | null
}

/**
 * Returns the campaign that ended most recently (max endDate where endDate <= now).
 * Assignment end is exclusive: "ended" means now >= end. All instants are interpreted in UTC.
 */
export function getLastEndedCampaign(
  campaigns: CampaignWithEndDate[] | null | undefined,
  now: Temporal.Instant = getLocalAsUtcInstant()
): { title: string, endDate: string | Temporal.Instant } | null {
  if (campaigns == null || campaigns.length === 0) { return null }
  const nowEpoch = now.epochMilliseconds
  let last: { title: string, endDate: string | Temporal.Instant } | null = null
  const toInstant = (s: string): Temporal.Instant =>
    Temporal.Instant.from(s.length === 10 && !s.includes('T') ? `${s}T23:59:59.999Z` : s)
  const toEndInstant = (end: string | Temporal.Instant | Date): Temporal.Instant =>
    typeof end === 'string' ? toInstant(end) : end instanceof Temporal.Instant ? end : Temporal.Instant.fromEpochMilliseconds(end.getTime())
  for (const c of campaigns) {
    const end = c.endDate
    if (end == null) { continue }
    const endInstant = toEndInstant(end)
    if (endInstant.epochMilliseconds > nowEpoch) { continue }
    const lastEpoch = last === null
      ? -Infinity
      : (typeof last.endDate === 'string' ? toInstant(last.endDate) : (last.endDate as Temporal.Instant)).epochMilliseconds
    if (endInstant.epochMilliseconds > lastEpoch) {
      last = {
        title: c.title ?? '',
        endDate: typeof end === 'string' ? end : end instanceof Temporal.Instant ? end : Temporal.Instant.fromEpochMilliseconds(end.getTime())
      }
    }
  }
  return last
}
